import { useRef, useEffect, useLayoutEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { MenuSelect } from 'components/Select/menuSelect';

import ColorField from './components/ColorField';
import FieldsRow from './components/FieldsRow';
import FontSizeControl from './components/FontSizeControl';
import ImageUploader from './components/ImageUploader';

const FONT_FAMILY_OPTIONS = [
  { label: 'Courier', value: 'Courier' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Trebuchet MS', value: 'Trebuchet MS' },
];

const StyleEditorPopover = ({ open, anchorRect, title, style, fields, onStyleChange = () => {}, onClose = () => {} }) => {
  const theme = useTheme();

  const [pos, setPos] = useState({ left: null, top: null });

  const handleChangeImage = file => {
    onStyleChange({ ...style, content: file?.data, type: file?.type, size: file?.size, name: file?.name });
  };

  const handleChange = (key, value) => {
    onStyleChange({ ...style, [key]: value });
  };

  const panelRef = useRef(null);

  useEffect(() => {
    const onDoc = e => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target)) return;
      // ignore clicks that occur inside the color picker's portal
      try {
        if (window.__styleEditorActivePicker && window.__styleEditorActivePicker.contains(e.target)) return;
      } catch (err) {}
      onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  useLayoutEffect(() => {
    if (!open) return;
    const width = 420;
    if (anchorRect && typeof window !== 'undefined') {
      const spaceRight = window.innerWidth - anchorRect.right - 8;
      const spaceLeft = anchorRect.left - 8;
      let left;
      if (spaceRight >= width) {
        left = Math.max(8, anchorRect.right + 8);
      } else if (spaceLeft >= width) {
        left = Math.max(8, anchorRect.left - width - 8);
      } else {
        // fallback: fit within viewport
        left = Math.max(8, Math.min(window.innerWidth - width - 8, anchorRect.right + 8));
      }

      // align top to anchorRect.top but keep within viewport
      const top = Math.max(8, Math.min(window.innerHeight - 120, anchorRect.top));
      setPos({ left, top });
    } else {
      // center fallback
      setPos({ left: null, top: null });
    }
  }, [open, anchorRect]);

  if (!open) return null;

  const panelSx = {
    position: 'fixed',
    width: 420,
    zIndex: 1400,
    backgroundColor: theme.palette.primary.white,
    border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
    borderRadius: theme.shape.customBorderRadius[1],
    boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.16)',
    padding: '12px 14px',
    cursor: 'default',
    textAlign: 'left',
  };

  if (pos.left != null && pos.top != null) {
    panelSx.left = pos.left;
    panelSx.top = pos.top;
  } else {
    panelSx.left = '50%';
    panelSx.top = '50%';
    panelSx.transform = 'translate(-50%, -50%)';
  }

  return (
    <Box ref={panelRef} data-style-editor-panel onMouseDown={e => e.stopPropagation()} sx={panelSx}>
      <Typography
        sx={{
          fontSize: theme.typography.customFontSize[2],
          fontWeight: theme.typography.customFontWeight[1],
          color: theme.palette.primary.main,
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
        }}
      >
        {title}
      </Typography>

      {(fields.includes('fontColor') && fields.includes('fontSize')) || (fields.includes('fontColor') && fields.includes('buttonColor')) ? (
        <FieldsRow fields={fields} style={style} handleChange={handleChange} />
      ) : (
        <>
          {/* Style: Background color, Font color, Button color, Font size, Image content */}
          {fields.includes('backgroundColor') && <ColorField label="Mono Color" value={style.backgroundColor} onChange={value => handleChange('backgroundColor', value)} />}

          {fields.includes('fontColor') && <ColorField label="Font Color" value={style.fontColor} onChange={value => handleChange('fontColor', value)} />}

          {fields.includes('buttonColor') && <ColorField label="Button Color" value={style.buttonColor} onChange={value => handleChange('buttonColor', value)} />}

          {fields.includes('fontSize') && (
            <Box sx={{ marginBottom: '12px' }}>
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.main,
                  fontWeight: theme.typography.customFontWeight[1],
                  marginBottom: '6px',
                }}
              >
                Font Size
              </Typography>
              <FontSizeControl value={style.fontSize} onChange={value => handleChange('fontSize', value)} />
            </Box>
          )}

          {fields.includes('uploadImage') && (
            <Box sx={{ marginBottom: '12px' }}>
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.main,
                  fontWeight: theme.typography.customFontWeight[1],
                  marginBottom: '6px',
                }}
              >
                Choose File
              </Typography>
              <ImageUploader title={title} value={style?.content} onChange={file => handleChangeImage(file)} />
            </Box>
          )}
        </>
      )}

      {/* Font Family */}
      {fields.includes('fontFamily') && (
        <Box>
          <Typography
            sx={{
              fontSize: theme.typography.customFontSize[1],
              color: theme.palette.primary.main,
              fontWeight: theme.typography.customFontWeight[1],
              marginBottom: '6px',
            }}
          >
            Font Family
          </Typography>
          <Box
            onMouseDown={e => {
              e.stopPropagation();
              try {
                window.dispatchEvent(new CustomEvent('styleEditor:closePicker'));
              } catch (err) {}
            }}
          >
            <MenuSelect width="100%" value={style.fontFamily} options={FONT_FAMILY_OPTIONS} onChange={obj => handleChange('fontFamily', obj.value)} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StyleEditorPopover;
